/**
 * A set of chips, any number of which can be on.
 *
 * The reports filtered through five hand-rolled `dropdown` panels that opened
 * on `:focus-within` and closed the moment focus left, so a keyboard reader
 * could tick one option and never a second. Chips have no open state to lose:
 * every option is on the page, each is a real checkbox, and the group is a
 * `fieldset` so its legend names what the ticks belong to.
 *
 * @module components/ui/CheckboxChipGroup
 */

import React, { useId } from "react";

import { useLockedRefusal } from "./EditLock";

export interface ChipOption {
  value: string;
  label: string;
  /** Extra context read after the label, such as wording the short label omits. */
  description?: string;
  "data-testid"?: string;
}

export interface CheckboxChipGroupProps {
  /** Names the group. Rendered as the fieldset's legend. */
  legend: string;
  options: readonly ChipOption[];
  /** The values currently on. */
  value: readonly string[];
  onChange: (value: readonly string[]) => void;
  /** Refuses every chip at once, as a native `fieldset disabled` does. */
  disabled?: boolean;
  /**
   * Why the group refuses. A disabled fieldset takes its chips out of the tab
   * order, so the reason is read from the group itself or nobody meets it.
   */
  disabledReason?: string;
  className?: string;
  "data-testid"?: string;
}

/**
 * @example
 * ```tsx
 * <CheckboxChipGroup
 *   legend="Discipline"
 *   options={disciplines}
 *   value={filters.schools}
 *   onChange={(next) => update("schools", next)}
 * />
 * ```
 */
export function CheckboxChipGroup({
  legend,
  options,
  value,
  onChange,
  disabled: ownDisabled = false,
  disabledReason: ownReason = undefined,
  className = "",
  "data-testid": testId,
}: CheckboxChipGroupProps): React.ReactElement {
  const { disabled, disabledReason } = useLockedRefusal(ownDisabled, { disabledReason: ownReason });
  const chosen = new Set(value);
  const idBase = useId();
  const reason = disabled && disabledReason ? disabledReason : undefined;
  const reasonId = reason ? `${idBase}-reason` : undefined;
  return (
    <fieldset
      className={className || undefined}
      data-testid={testId}
      disabled={disabled}
      aria-describedby={reasonId}
    >
      <legend className="mb-1 text-xs font-semibold text-muted uppercase tracking-wide">
        {legend}
      </legend>
      {reason ? (
        <p id={reasonId} className="mb-1 text-xs text-muted">
          {reason}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const on = chosen.has(option.value);
          const descriptionId = option.description
            ? `${idBase}-${option.value}-description`
            : undefined;
          return (
            // A fragment, not a single label: the description has to sit
            // outside the label element, or its text joins the checkbox's
            // accessible name (every descendant of a label counts, sr-only
            // or not) instead of staying in the description.
            <React.Fragment key={option.value}>
              <label
                // The chip is the label, so the whole target is clickable
                // rather than the 16px box alone: WCAG 2.5.8 wants 24px and a
                // bare checkbox beside text gives the pointer only the box.
                className={[
                  "inline-flex cursor-pointer items-center gap-2 rounded-box border px-3 py-1.5 text-sm",
                  on
                    ? "border-primary bg-accent-soft font-semibold"
                    : "border-base-300 bg-base-100 hover:bg-surface-hover",
                ].join(" ")}
                data-testid={option["data-testid"]}
              >
                <input
                  type="checkbox"
                  className="checkbox checkbox-xs"
                  checked={on}
                  onChange={() => {
                    const next = new Set(chosen);
                    if (on) {
                      next.delete(option.value);
                    } else {
                      next.add(option.value);
                    }
                    onChange([...next]);
                  }}
                  aria-describedby={descriptionId}
                  data-testid={testId ? `${testId}-opt-${option.value}` : undefined}
                />
                {option.label}
              </label>
              {option.description ? (
                <span id={descriptionId} className="sr-only">
                  {option.description}
                </span>
              ) : null}
            </React.Fragment>
          );
        })}
      </div>
    </fieldset>
  );
}
